/* eslint-disable no-param-reassign */
import * as yup from 'yup';
import { TestSessionDto, TestSessionInstrumentDto } from 'modules/InstrumentsQualityControl/models';
import UpdateVisualInspectionToInstrumentInput from 'modules/InstrumentsQualityControl/models/UpdateVisualInspectionToInstrumentInput';
import ReworkRequriedWidget from '../widgets/ReworkRequriedWidget';
import ConditionalField from '../widgets/ConditionalField';
import InstrumentResultWidget from '../widgets/InstrumentResultWidget';
import CaseField from '../widgets/CaseField';
import ReviewDisplayField from '../widgets/ReviewDisplayField';
import ReviewDisplayTextField from '../widgets/ReviewDisplayTextField';
import RubberFeetWidget from '../widgets/RubberFeetWidget';
import { formatBool } from '../utils';

function noop<T>(id: T) {
  return id;
}

function configureYesNoNotApplicableInput(item) {
  const { title } = item;
  delete item.title;

  item.properties.value.title = title;
  item.properties.notApplicableComment.title = `${title} Not Applicable Comment`;
}

export const schema = sch => {
  sch.properties.flexReworkInstrument.type = 'string';
  sch.properties.flexReworkInstrument.type = 'string';
  sch.properties.qualityEventRequired.type = 'string';

  configureYesNoNotApplicableInput(sch.properties.softwareUploadRequired);
  configureYesNoNotApplicableInput(sch.properties.currentSoftwareNowInstalled);

  return sch;
};

function configureDoorUi() {
  const conditionalField = {
    'ui:field': ConditionalField,
    predicate: values =>
      values.door.doesTheDoorOpenSmoothlyWithoutObstructionAndDislocation?.toString() === 'false' ||
      values.door.doesTheDoorCloseSmoothlyWithoutObstructionAndDislocationAndCanSelfClose?.toString() ===
        'false',
  };
  return {
    'ui:grid:xs': 6,
    'ui:card': true,

    doesTheDoorOpenSmoothlyWithoutObstructionAndDislocation: {
      'ui:widget': ReviewDisplayField,
    },
    doesTheDoorCloseSmoothlyWithoutObstructionAndDislocationAndCanSelfClose: {
      'ui:widget': ReviewDisplayField,
    },
    reworkRequired: {
      'ui:widget': ReworkRequriedWidget,
      ...conditionalField,
    },
    reworkCarriedOutBy: {
      'ui:widget': ReviewDisplayField,
      'ui:options': {
        select: ({ all }) => all,
      },
      ...conditionalField,
    },
    verifiedBy: {
      'ui:widget': ReviewDisplayField,
      'ui:options': {
        select: ({ all }) => all,
      },
      ...conditionalField,
    },
    theDoorShouldOpenAndCloseSmoothlyWithoutDislocatingAndAcceptableGap: {
      'ui:widget': ReviewDisplayField,
    },
    theDoorShouldNotBeLooseOrDifficultToOpenClose: {
      'ui:widget': ReviewDisplayField,
    },
  };
}

function configureRubberFeetUi() {
  const conditionalField = {
    'ui:field': ConditionalField,
    predicate: values =>
      values.rubberFeet.areAllFourFeetFirmlyAttachedToTheInstrumentCasing?.toString() === 'false',
  };
  return {
    'ui:grid:xs': 6,
    'ui:card': true,
    areAllFourFeetFirmlyAttachedToTheInstrumentCasing: {
      'ui:widget': ReviewDisplayField,
    },
    ifFeetAreLoosePleaseIndicateWhichFeet: {
      'ui:widget': RubberFeetWidget,
      'ui:options': {
        readOnly: true,
      },
      ...conditionalField,
    },
    reworkRequired: {
      'ui:widget': ReviewDisplayField,
      ...conditionalField,
    },
    reworkCarriedOutBy: {
      'ui:widget': ReviewDisplayField,
      'ui:options': {
        select: ({ all }) => all,
      },
      ...conditionalField,
    },
    verifiedBy: {
      'ui:widget': ReviewDisplayField,
      'ui:options': {
        select: ({ all }) => all,
      },
      ...conditionalField,
    },
    allFourRubberFeetShouldBeFirmlyAttachedToTheInstrumentCasing: {
      'ui:widget': ReviewDisplayField,
    },
    theInstrumentShouldBeLevelWhenPlacedOnAStableLevelSurface: {
      'ui:widget': ReviewDisplayField,
    },
  };
}

export const uiSchema = ui => {
  ui['ui:order'] = [
    'serialNumberVerifiedAgainstLabel',
    'visualInspectionOutcome',
    'visualInspectionStatus',
    'goodsReceivedNumber',
    'flexReworkInstrument',
    'detailsVerifiedBy',
    'currentReleasedSoftware',
    'softwareVersionInstalledOnInstrument',
    'softwareUploadRequired',
    'currentSoftwareNowInstalled',
    'carriedOutBy',
    'case',
    'labels',
    'door',
    'screen',
    'rubberFeet',
    'stripDetectionAndNfcOperation',
    '*',
  ];
  ui.qualityEventRequired = {
    'ui:grid:xs': 6,
    format: formatBool,
    'ui:widget': ReviewDisplayField,
  };
  ui.qualityEventNumber = {
    'ui:field': ConditionalField,
    'ui:widget': ReviewDisplayField,
    predicate: values => values?.qualityEventRequired?.toString() === 'true',
  };

  ui.instrumentId = {
    'ui:widget': 'hidden',
  };

  ui.serialNumberVerifiedAgainstLabel = {
    'ui:widget': ReviewDisplayField,
    'ui:grid:xs': 6,
  };

  ui.visualInspectionStatus = {
    'ui:widget': 'hidden',
    'ui:field': InstrumentResultWidget,
    'ui:grid:xs': 6,
  };

  ui.goodsReceivedNumber = {
    'ui:widget': ReviewDisplayField,
    'ui:options': {
      select: (current: TestSessionDto) => current.instruments?.map(x => x.visualInspection),
    },
    'ui:grid:xs': 6,
    format: value => value,
  };

  ui.visualInspectionOutcome = {
    'ui:widget': ReviewDisplayField,
    'ui:grid:xs': 6,
  };

  ui.flexReworkInstrument = {
    'ui:grid:xs': 6,
    'ui:widget': ReviewDisplayTextField,
    format: formatBool,
  };

  ui.detailsVerifiedBy = {
    'ui:widget': ReviewDisplayField,
    'ui:options': {
      select: ({ all }) => all,
    },
  };
  ui.currentReleasedSoftware = {
    'ui:grid:xs': 6,
    'ui:widget': ReviewDisplayField,
    format: value => value,
  };
  ui.softwareVersionInstalledOnInstrument = {
    'ui:grid:xs': 6,
    'ui:widget': ReviewDisplayField,
    format: value => value,
  };
  ui.softwareUploadRequired = {
    'ui:title': 'Software Upload Required',
    format: value => value,
    'ui:collapse': true,
    value: { 'ui:widget': ReviewDisplayField, format: formatBool },
    notApplicableComment: {
      'ui:field': ConditionalField,
      'ui:widget': ReviewDisplayField,
      predicate: values => values.softwareUploadRequired.value === 'NotApplicable',
      format: value => value,
    },
  };
  ui.currentSoftwareNowInstalled = {
    'ui:field': ConditionalField,
    'ui:collapse': true,
    'ui:widget': ReviewDisplayField,
    predicate: values => values.softwareUploadRequired.value === 'Yes',
    value: {
      predicate: values => values.softwareUploadRequired.value === 'Yes',
      'ui:field': ConditionalField,
      'ui:widget': ReviewDisplayField,
      format: formatBool,
    },
    notApplicableComment: {
      'ui:field': ConditionalField,
      'ui:widget': ReviewDisplayField,
      predicate: values => values.currentSoftwareNowInstalled.value === 'NotApplicable',
    },
  };
  ui.carriedOutBy = {
    'ui:field': ConditionalField,
    predicate: values => values.softwareUploadRequired.value === 'Yes',
    'ui:widget': ReviewDisplayField,
    'ui:options': {
      select: ({ current }) => current,
    },
  };
  ui.case = {
    'ui:field': CaseField,
    'ui:grid:xs': 6,
    'ui:card': true,
    instrumentCasingShouldBeInspectedForMajorDefects: {
      'ui:widget': ReviewDisplayField,
    },
    theWhiteTopCasingShouldBeSecurelyAndEvenlyAttachedToTheBlackBaseUnit: {
      'ui:widget': ReviewDisplayField,
    },
  };

  const caseDisable = {
    'ui:disable': values =>
      values.case?.instrumentCasingShouldBeInspectedForMajorDefects?.toString() === 'false' ||
      values.case?.theWhiteTopCasingShouldBeSecurelyAndEvenlyAttachedToTheBlackBaseUnit?.toString() ===
        'false',
  };
  ui.door = {
    ...configureDoorUi(),
    ...caseDisable,
  };
  ui.screen = {
    ...caseDisable,
    'ui:grid:xs': 6,
    'ui:card': true,
    theScreenShouldNotBeCrackedScratchedOrChipped: {
      'ui:widget': ReviewDisplayField,
    },
    theScreenShouldFitFlushWithTheInstrumentCasing: {
      'ui:widget': ReviewDisplayField,
    },
    whenTheInstrumentIsPoweredOnTheScreenShouldProduceAColourDisplayWithNoObviousDefects: {
      'ui:widget': ReviewDisplayField,
    },
    whenTheInstrumentIsPoweredOnTheScreenShouldProvideTouchscreenFunctionality: {
      'ui:widget': ReviewDisplayField,
    },
  };

  ui.rubberFeet = {
    ...caseDisable,
    ...configureRubberFeetUi(),
  };
  ui.labels = {
    ...caseDisable,
    'ui:grid:xs': 6,
    'ui:card': true,
    serialNumberOnLabelMatchesSerialNumberInAboutScreen: {
      'ui:widget': ReviewDisplayField,
    },
    theLumiradxLogoShouldBeVisibleOnTheTopSideOfTheInstrumentBetweenTheScreenAndTheDoor: {
      'ui:widget': ReviewDisplayField,
    },
    theNfcIconShouldBeVisibleOnTheRightHandSideOfTheInstrument: {
      'ui:widget': ReviewDisplayField,
    },
    labelShouldBeFreeFromPrintDefectsAndFormatAndContentShouldMatchThatSpecifiedInTheCurrentRevision: {
      'ui:widget': ReviewDisplayField,
    },
  };
  ui.stripDetectionAndNfcOperation = {
    ...caseDisable,
    'ui:card': true,
    'ui:grid:xs': 6,
    instrumentDetectsStrips: {
      'ui:widget': ReviewDisplayField,
    },
    instrumentRecognisesRfidTag: {
      'ui:widget': ReviewDisplayField,
    },
    instrumentSuccessfullyUploadsLotCalibrationFiles: {
      'ui:widget': ReviewDisplayField,
    },
  };
  ui.plaqueCalibration = {
    ...caseDisable,
    'ui:card': true,
    calibrationValue: {
      'ui:grid:xs': 4,
      'ui:widget': ReviewDisplayField,
      format: value => value,
    },
    meanDifferenceValue: {
      'ui:grid:xs': 4,
      'ui:widget': ReviewDisplayField,
      format: value => value,
    },
    biasValue: {
      'ui:grid:xs': 4,
      'ui:widget': ReviewDisplayField,
      format: value => value,
    },
  };
  ui.qualityEventRequired = {
    'ui:widget': ReviewDisplayTextField,
    format: formatBool,
  };

  ui.qualityEventNumber = {
    'ui:field': ConditionalField,
    predicate: values => values?.qualityEventRequired?.toString() > 'true',
    'ui:widget': ReviewDisplayField,
    format: value => value,
  };

  ui.plannedDeviationNumbers = {
    'ui:field': ConditionalField,
    predicate: values => values?.plannedDeviationNumbers?.length > 0,
    'ui:options': {
      addable: false,
      removable: false,
      orderable: false,
    },
    items: {
      'ui:widget': ReviewDisplayTextField,
    },
  };

  return ui;
};

export const mapToEdit = () => (
  current: TestSessionInstrumentDto
): UpdateVisualInspectionToInstrumentInput => {
  const inspection = current?.visualInspection || {};
  return {
    ...inspection,
    instrumentId: current?.id,
  } as UpdateVisualInspectionToInstrumentInput;
};

export const fields = noop;

export const widgets = noop;

export const validation = () => yup.object({});

export const empty = noop;
